/**
 * Resizable Header Component
 * 
 * Excel-like column resizing functionality:
 * - Mouse cursor changes on column border hover
 * - Drag to resize column width
 * - Respects min/max width constraints  
 * - Visual feedback during resize
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import type { ColumnDefinition, TableDataRow } from '../types/table';

interface ResizableHeaderProps<T extends TableDataRow = TableDataRow> {
  column: ColumnDefinition<T>;
  children: React.ReactNode;
  onResize: (columnId: string, width: number) => void;
  className?: string;
  onClick?: () => void;
  style?: React.CSSProperties;
}

export const ResizableHeader = <T extends TableDataRow = TableDataRow>({
  column,
  children,
  onResize,
  className = '',
  onClick,
  style: externalStyle
}: ResizableHeaderProps<T>) => {
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const headerRef = useRef<HTMLTableCellElement>(null);
  const resizeHandleRef = useRef<HTMLDivElement>(null);

  /**
   * Handle mouse down on resize handle
   */
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent column click/sort
    
    setIsResizing(true);
    setStartX(e.clientX);
    setStartWidth(column.width);
    
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [column.width]);

  /**
   * Handle mouse move during resize
   */
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;

    const deltaX = e.clientX - startX;
    const newWidth = startWidth + deltaX;
    
    // Apply constraints
    const constrainedWidth = Math.max(
      column.minWidth,
      column.maxWidth ? Math.min(column.maxWidth, newWidth) : newWidth
    );
    
    onResize(column.id, constrainedWidth);
  }, [isResizing, startX, startWidth, column.minWidth, column.maxWidth, column.id, onResize]);

  /**
   * Handle mouse up to end resize
   */
  const handleMouseUp = useCallback(() => {
    if (!isResizing) return;
    
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [isResizing]);

  /**
   * Attach/detach global mouse events
   */
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  /**
   * Handle header click (for sorting)
   */
  const handleHeaderClick = useCallback(() => {
    if (!isResizing && onClick) {
      onClick();
    }
  }, [isResizing, onClick]);

  return (
    <th
      ref={headerRef}
      className={`${className} relative group`}
      style={{ width: `${column.width}px`, ...externalStyle }}
      onClick={handleHeaderClick}
    >
      {children}
      
      {/* Resize Handle */}
      {column.resizable && (
        <div
          ref={resizeHandleRef}
          className={`absolute top-0 right-0 w-1 h-full cursor-col-resize group-hover:bg-teal-300 transition-colors ${
            isResizing ? 'bg-teal-500' : 'bg-transparent hover:bg-teal-400'
          }`}
          onMouseDown={handleMouseDown}
          style={{ zIndex: 10 }}
        >
          {/* Wider hit area for easier grabbing */}
          <div className="absolute inset-y-0 -left-1 -right-1"></div>
        </div>
      )}
      
      {/* Visual feedback during resize */}
      {isResizing && (
        <div className="absolute top-0 right-0 w-px h-full bg-teal-500 shadow-lg"></div>
      )}
    </th>
  );
};